<template lang="pug">
#other-office-online-meeting-register(v-cloak)
  .box
    .box-header.with-border.register-header
      .title
        span オンライン会議新規作成

    .box-body.register-body
      //- 日付・時間
      FormEventDatetimePicker(v-model="startDatetime" :date.sync="startDate" :startTime.sync="startTimeFrom" :endTime.sync="startTimeTo"
        key-name="日時" name="start-and-end-date" placeholder="日時" :validation-rules="{ required: true }" required)
      //- 会議相手
      FormInputSelect(v-model="subFacilityId" key-name="会議相手" name="sub-facility-id"
        :options="subFacilityIdOptions" :validation-rules="{ required: true }" :styles="{ 'width': '385px', 'max-width': '385px' }" required)
      //- 件名
      FormInput(v-model="title" key-name="件名" name="title" type="text" placeholder="件名"
        :maxlength="100" :validation-rules="{ required: true, max: 100 }" :styles="{ maxWidth: '680px', width: '680px' }" required)
      //- 内容
      FormTextarea(v-model="text" key-name="内容" name="text" placeholder="内容" :maxlength="1000" :rows="7"
        :validation-rules="{ required: true, max: 1000 }" :styles="{ maxWidth: '680px', width: '680px' }" required)
      //- 会議案内送付先メールアドレス
      .register-row
        .register-group
          .register-key
            .d-flex.flex-column
              span.key-name 会議案内送付先メールアドレス
              span.warning-message(style="font-size: 14px;") ※ 会議相手が通知設定で登録しているメールアドレスには未記載でも自動的にメール送付されます。
              span.warning-message(style="font-size: 14px;") ※ 案内送付数に関わらず、参加者は最大5名までです。
          .register-value
            .d-flex.flex-column
              .mail-address-input.mb-2.d-flex.flex-row.align-items-center(v-for="(address, index) in notificationMailAddress")
                b-form-input.mail-address.mr-1(
                  v-model="notificationMailAddress[index]" :name="'notification-mail-address-' + index"
                  size="md" type="email" placeholder="会議案内送付先メールアドレス" maxlength="100"
                  v-validate="{ email: true, max: 100 }" :data-vv-as="(index + 1) + '件目の会議案内送付先メールアドレス'")
                b-button.mail-address-delete-btn(@click="deleteMailAddress(notificationMailAddress, index)")
                  .trash-icon
        .register-group
          b-button.add-mailaddress-btn.mb-2(@click="addMailAddress(notificationMailAddress)"
            :class="{ 'disable': _disableAddMailAddress }" :disabled="_disableAddMailAddress") 追加
          .error-messages-contents(v-for="(item, index) in notificationMailAddress")
            .error-messages(v-for="error in errors.collect('notification-mail-address-' + index)")
              .error-message {{ error }}

      //- エラーメッセージ
      .register-row(v-show="errorMessage")
        .register-group
          .register-value
            .error-message {{ errorMessage }}

    .box-footer.with-border.register-footer
      ValidationAlert(v-model="showValidationAlert")
      .buttons
        button.btn.register-btn(@click="showConfirmModal" type="button") 登録
        button.btn.cancel-btn(@click="gotoMeetingList" type="button") キャンセル

  OkCancelModal(v-model="showRegisterModal" title="オンライン会議を登録しますか？" @ok="registerMeeting")
  OkModal(v-model="showRegisterSuccessModal" title="登録に成功しました。" @ok="gotoMeetingList")
  
  PageLoading(:isBusy="isBusy" :message="loadingMessage")
</template>

<script lang="ts">
import { Component, Inject, Vue } from "vue-property-decorator";
import { userStore } from "@/store/store";

import { PcUrl } from "@/commons/constants/PcUrl";

import OtherOfficeOnlineMeetingsService, { OtherOfficeOnlineMeetingPostModel } from "@/commons/services/OtherOfficeOnlineMeetings";
import OtherOfficeConnectionsService, { OtherOfficeConnectionGetModel } from "@/commons/services/OtherOfficeConnections";

import FormEventDatetimePicker from "@/pc/components/forms/FormEventDatetimePicker.vue";
import FormInputSelect from "@/pc/components/forms/FormInputSelect.vue";
import FormInput from "@/pc/components/forms/FormInput.vue";
import FormTextarea from "@/pc/components/forms/FormTextarea.vue";

import PageLoading from "@/pc/basics/PageLoading.vue";
import ValidationAlert from "@/pc/basics/ValidationAlertByValidator.vue";
import OkCancelModal from "@/pc/basics/OkCancelModal.vue";
import OkModal from "@/pc/basics/OkModal.vue";

/**
 * ### 他事業所連携 オンライン会議登録画面
 */
@Component({
  components: {
    FormEventDatetimePicker,
    FormInputSelect,
    FormInput,
    FormTextarea,
    PageLoading,
    ValidationAlert,
    OkCancelModal,
    OkModal
  },
  $_veeValidate: { validator: "new" }
})
export default class OnlineMeetingRegister extends Vue {
  /** 待機者ラベルサービス */
  @Inject("OtherOfficeOnlineMeetingsService") meetingsService!: OtherOfficeOnlineMeetingsService;
  /** 他事業所連携管理サービス */
  @Inject("OtherOfficeConnectionsService") otherOfficeConnectionsService!: OtherOfficeConnectionsService;

  /** ページ全体ローディング表示可否 */
  isBusy: boolean = false;
  /** ローディングメッセージ */
  loadingMessage: string = "";

  /** 日時 */
  startDatetime: number | null = null;
  /** 日付 */
  startDate: string = "";
  /** 開始時刻 */
  startTimeFrom: string = "";
  /** 終了時刻 */
  startTimeTo: string = "";

  /** 会議相手施設ID */
  subFacilityId: number | null = null;
  /** 会議相手施設ID選択肢 */
  subFacilityIdOptions: Array<{ value: number | string; label: string }> = [];
  /** 件名 */
  title: string = "";
  /** 内容 */
  text: string = "";
  /** 会議案内送付先メールアドレス */
  notificationMailAddress: string[] = [""];

  /** 登録確認モーダル表示可否 */
  showRegisterModal: boolean = false;
  /** 登録完了モーダル表示可否 */
  showRegisterSuccessModal: boolean = false;

  /** バリデーションアラート表示可否 */
  showValidationAlert: boolean = false;
  /** エラーメッセージ */
  errorMessage: string = "";

  /**
   * ### vue lifecycle created
   */
  public async mounted(): Promise<void> {
    await this.createView();
    return;
  }

  /**
   * ### 画面情報作成
   */
  public async createView(): Promise<void> {
    this.isBusy = true;
    this.loadingMessage = "読込み中...";

    const otherOfficeConnections: Array<OtherOfficeConnectionGetModel> = await this.getOtherOfficeConnections();
    const subFacilityIdOptions: Array<{ value: number; label: string }> = this.createSubFacilityIdOptions(otherOfficeConnections);
    this.subFacilityIdOptions = subFacilityIdOptions;

    const eventDate: string = this.$dateUtil.convertDateToString(new Date(), "yyyy-mm-dd");
    this.startDate = eventDate;
    this.startTimeFrom = "10:00:00";
    this.startTimeTo = "11:00:00";

    this.isBusy = false;

    return;
  }

  /** ### 他事業所連携管理を取得する */
  public async getOtherOfficeConnections(): Promise<Array<OtherOfficeConnectionGetModel>> {
    const response: Array<OtherOfficeConnectionGetModel> = await this.otherOfficeConnectionsService.gets(userStore);
    return response;
  }

  /** ### 会議相手施設ID選択肢を作成する */
  public createSubFacilityIdOptions(connections: Array<OtherOfficeConnectionGetModel>): Array<{ value: number; label: string }> {
    const subFacilityIdOptions: Array<{ value: number; label: string }> = [];
    for (const connection of connections) {
      subFacilityIdOptions.push({
        value: connection.registerdFacilityId,
        label: `${connection.officeName}`
      });
    }
    return subFacilityIdOptions;
  }

  /** ### 削除ボタン押下イベント */
  public deleteMailAddress(target: Array<string>, index: number): void {
    if (1 < target.length) {
      target.splice(index, 1);
    } else {
      target.splice(index, 1);
      target.push("");
    }
    return;
  }

  /**
   * ### 曜日情報追加できないか
   */
  get _disableAddMailAddress(): boolean {
    if (3 <= this.notificationMailAddress.length) {
      return true;
    } else {
      return false;
    }
  }

  /** ### 追加ボタン押下イベント */
  public addMailAddress(target: Array<string>): void {
    if (this._disableAddMailAddress) {
      return;
    }
    target.push("");
    return;
  }

  public async showConfirmModal(): Promise<void> {
    const validationResult: boolean = await this.$validator.validateAll();
    if (validationResult == true) {
      this.showValidationAlert = false;
      this.showRegisterModal = true;
    } else {
      this.showValidationAlert = true;
    }
    return;
  }

  public async registerMeeting(): Promise<void> {
    this.isBusy = true;
    this.loadingMessage = "登録中...";

    const requestBody: OtherOfficeOnlineMeetingPostModel = this.createRequestBody();
    await this.meetingsService.post(userStore, this.subFacilityId as number, requestBody);

    this.showRegisterSuccessModal = true;
    this.isBusy = false;

    return;
  }

  public createRequestBody(): OtherOfficeOnlineMeetingPostModel {
    const startDatetime = this.$dateUtil.convertDateToNumber(new Date(`${this.startDate}T${this.startTimeFrom}+09:00`), "yyyymmddhhMMss000");
    const endDatetime = this.$dateUtil.convertDateToNumber(new Date(`${this.startDate}T${this.startTimeTo}+09:00`), "yyyymmddhhMMss000");
    // 通知案内のメールアドレスは空もありえるので、空文字やnullの場合は除外する
    const notificationMailAddress: string[] = [];
    for (const mailaddress of this.notificationMailAddress) {
      if (mailaddress) {
        notificationMailAddress.push(mailaddress);
      }
    }
    return {
      startDatetime: startDatetime,
      endDatetime: endDatetime,
      title: this.title,
      text: this.text,
      notificationMailAddress: notificationMailAddress
    };
  }

  /**
   * ### オンライン会議一覧に遷移する
   */
  public gotoMeetingList(): void {
    this.$router.push(PcUrl.OtherOfficeOnlineMeetings);
    return;
  }
}
</script>

<style lang="scss" scoped>
@import "../../../styles/pc/_colors.scss";
@import "../../../styles/pc/_mixin.scss";

#other-office-online-meeting-register {
  max-width: 740px;
  margin-left: auto;
  margin-right: auto;

  .mail-address-input {
    .mail-address {
      min-width: 300px;
    }
  }
  .mail-address-input,
  .add-mailaddress-btn {
    .btn {
      font-size: 18px !important;
      height: 38px !important;
      padding: 4px 8px !important;
    }
    .mail-address-delete-btn {
      padding: 2px 5px !important;
      .trash-icon {
        @include svg-icon("../../../assets/trash.svg", $font-base-color, 28px, 28px);
      }
    }
  }
  .add-mailaddress-btn {
    width: 80px;
  }

  .register-btn,
  .cancel-btn {
    width: 120px;
  }
}
</style>
